import { nanoid } from "nanoid";
import {
  useState,
  useCallback,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from "react";

import type { Condition } from "@/types/screener-builder";

export interface ChoiceRuleWithId extends Condition {
  id: string;
}

interface UseChoiceRulesWithDuplicatesReturn {
  choiceRulesWithIds: ChoiceRuleWithId[];
  duplicateErrorTempIds: Set<string>;
  updateOptionText: (tempId: string, text: string) => void;
  addOption: () => void;
  deleteOption: (tempId: string) => void;
  updateChoiceRules: Dispatch<SetStateAction<ChoiceRuleWithId[]>>;
}

const addTempIdsToChoiceRules = (rules: Condition[]): ChoiceRuleWithId[] => {
  return rules.map((rule) => ({
    ...rule,
    id: nanoid(),
  }));
};

const calculateDuplicateErrors = (rules: ChoiceRuleWithId[]): Set<string> => {
  const duplicateErrorTempIds = new Set<string>();

  rules.forEach((currentOption, index) => {
    const otherOptions = rules.filter((_, i) => i !== index);
    const isDuplicate = otherOptions.some(
      (opt) =>
        opt.optionText.trim().toLowerCase() ===
        currentOption.optionText.trim().toLowerCase(),
    );

    if (isDuplicate) {
      duplicateErrorTempIds.add(currentOption.id);
    }
  });

  return duplicateErrorTempIds;
};

export const useChoiceRulesWithDuplicates = (
  initialRules: Condition[] = [],
): UseChoiceRulesWithDuplicatesReturn => {
  const initializedRules = useMemo(
    () => addTempIdsToChoiceRules(initialRules),
    // We use this memo to consistently initialize state on first load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [choiceRulesWithIds, setChoiceRulesWithIds] =
    useState<ChoiceRuleWithId[]>(initializedRules);
  const [duplicateErrorTempIds, setDuplicateErrorTempIds] = useState<
    Set<string>
  >(() => calculateDuplicateErrors(initializedRules));

  const updateRulesAndRecalculateDuplicates = useCallback(
    (newRulesOrUpdater: SetStateAction<ChoiceRuleWithId[]>) => {
      setChoiceRulesWithIds((prevRules) => {
        const newRules =
          typeof newRulesOrUpdater === "function"
            ? newRulesOrUpdater(prevRules)
            : newRulesOrUpdater;
        setDuplicateErrorTempIds(calculateDuplicateErrors(newRules));
        return newRules;
      });
    },
    [],
  );

  const updateOptionText = useCallback(
    (tempId: string, text: string) => {
      const newRules = choiceRulesWithIds.map((rule) =>
        rule.id === tempId ? { ...rule, optionText: text } : rule,
      );

      updateRulesAndRecalculateDuplicates(newRules);
    },
    [choiceRulesWithIds, updateRulesAndRecalculateDuplicates],
  );

  const addOption = useCallback(() => {
    updateRulesAndRecalculateDuplicates([
      ...choiceRulesWithIds,
      {
        optionText: `Option ${choiceRulesWithIds.length + 1}`,
        qualifier: undefined,
        id: nanoid(),
      },
    ]);
  }, [choiceRulesWithIds, updateRulesAndRecalculateDuplicates]);

  const deleteOption = useCallback(
    (tempId: string) => {
      if (choiceRulesWithIds.length === 1) {
        return;
      }

      updateRulesAndRecalculateDuplicates(
        choiceRulesWithIds.filter((rule) => rule.id !== tempId),
      );
    },
    [choiceRulesWithIds, updateRulesAndRecalculateDuplicates],
  );

  return {
    choiceRulesWithIds,
    duplicateErrorTempIds,
    updateOptionText,
    addOption,
    deleteOption,
    updateChoiceRules: updateRulesAndRecalculateDuplicates,
  };
};
